import React, {useCallback, useContext, useState} from 'react';
import Modal, {ModalFooter} from "@amzn/meridian/modal";
import Row from "@amzn/meridian/row";
import Button from "@amzn/meridian/button";
import {injectIntl} from "react-intl";
import Column from "@meridian/components/column";
import Text from "@amzn/meridian/text";
import Alert from "@amzn/meridian/alert"
import {
    checkModalValidationsForTriggeringMHEValidation,
    isDurationMoreThanTwelveHours,
    isFromAndToDateTimeEntered,
    isStartTimeLaterThanEndTime
} from "../../util/Validations";
import DatePicker from "@amzn/meridian/date-picker";
import TimePicker from "@amzn/meridian/time-picker";
import addDays from "date-fns/addDays"
import Textarea from "@amzn/meridian/textarea";
import {formatDate, getDateInIsoFormat, getNodeId} from "../../util/Util";
import {GlobalStateContext} from "../../global/context/GlobalStateContext";
import {getLocalizedMessage} from "../../util/Localization";
import {UTC_TIMEZONE_OFFSET_STRING} from "../../util/Constants";

const NewValidationModal = (props) => {

    const {dispatch, selectedDevice, intl, locale} = props;
    const { showCustomAlert } = useContext(GlobalStateContext);
    const [valueDateFrom, setValueDateFrom] = useState("")
    const [valueTimeFrom, setValueTimeFrom] = useState("")
    const [valueDateTo, setValueDateTo] = useState("")
    const [valueTimeTo, setValueTimeTo] = useState("")
    const [remarks, setRemarks] = useState("")
    const [showWarning, setShowWarning] = useState(false)
    const yesterday = getDateInIsoFormat(addDays(new Date(), -1));
    const today = getDateInIsoFormat(new Date());

    const onCloseModal = useCallback(() =>
        props.onStateChange(false), [])

    function onClickCancel() {
        setShowWarning(false);
        props.onStateChange(false)
        setInputState();
    }

    const stateValues = {
        "startTime": Date.parse(formatDate(valueDateFrom) + " " + valueTimeFrom + UTC_TIMEZONE_OFFSET_STRING),
        "endTime": Date.parse(formatDate(valueDateTo) + " " + valueTimeTo + UTC_TIMEZONE_OFFSET_STRING),
        "remarks": remarks
    }

    const dateTimeInput = {
        "valueDateFrom": valueDateFrom,
        "valueDateTo": valueDateTo,
        "valueTimeFrom": valueTimeFrom,
        "valueTimeTo": valueTimeTo
    }

    const triggerMHEValidation = useCallback(async (stateValues) => {
        setShowWarning(true);
        if (checkModalValidationsForTriggeringMHEValidation(stateValues, dateTimeInput)) {
            const triggerData = {
                "nodeId": getNodeId(),
                "macAddress": selectedDevice.mac,
                "startTime": stateValues.startTime,
                "endTime": stateValues.endTime,
                "validationAttributes": {
                    "remarks": stateValues.remarks
                },
                "assetType": selectedDevice.assetType,
                "assetSubType": selectedDevice.assetSubType,
                "deviceType": selectedDevice.deviceType,
                "deviceName": selectedDevice.name
            }
            dispatch({type: "TRIGGER_VALIDATION", data: {triggerData, dispatcher: dispatch, showCustomAlert: showCustomAlert}});
            props.onStateChange(false);
            setShowWarning(false);
            setInputState();
        }
    })

    function setInputState() {
        // setting state of input fields as empty to have empty inputs on clicking Add Device again
        setValueDateFrom("")
        setValueTimeFrom("")
        setValueTimeTo("")
        setValueDateTo("")
        setRemarks("")
    }

    return (
        <Row>
            <Modal
                title={"New Validation"}
                open={props.state}
                onClose={onCloseModal}
                scrollContainer="viewport"
                closeLabel="Close"
                bodySpacingInset="medium">
                <Column spacing={"medium"} spacingInset={"medium"}>
                    <Column spacing="200">
                        <Text tag="label" type="b200" color="primary" htmlFor="my-input">
                            {getLocalizedMessage({intl, stringId: "modal.validationstart"})}
                        </Text>
                        <Row>
                            <DatePicker
                                value={valueDateFrom}
                                onChange={setValueDateFrom}
                                label="Date"
                                presets={[
                                    {label: "Yesterday", value: yesterday},
                                    {label: "Today", value: today}
                                ]}
                                monthsInView={1}
                                locale={locale}
                            />
                            <TimePicker
                                placeholder={getLocalizedMessage({intl, stringId: "timepicker.placeholder"})}
                                value={valueTimeFrom}
                                onChange={setValueTimeFrom}
                                invalidInputMessage={getLocalizedMessage({intl, stringId: "timepicker.invalidinput"})}
                                width={300}
                                format={{ hourCycle: "h23", hour: "2-digit", minute: "2-digit" }}
                                showIcon={false}
                                label={getLocalizedMessage({intl, stringId: "timepicker.label"})}
                            />
                        </Row>
                    </Column>
                    <Column spacing="200">
                        <Text tag="label" type="b200" color="primary" htmlFor="my-input">
                            {getLocalizedMessage({intl, stringId: "modal.validationend"})}
                        </Text>
                        <Row>
                            <DatePicker
                                value={valueDateTo}
                                onChange={setValueDateTo}
                                label="Date"
                                presets={[
                                    {label: "Yesterday", value: yesterday},
                                    {label: "Today", value: today},
                                ]}
                                monthsInView={1}
                                locale={locale}
                            />
                            <TimePicker
                                placeholder={getLocalizedMessage({intl, stringId: "timepicker.placeholder"})}
                                value={valueTimeTo}
                                onChange={setValueTimeTo}
                                invalidInputMessage={getLocalizedMessage({intl, stringId: "timepicker.invalidinput"})}
                                width={300}
                                format={{ hourCycle: "h23", hour: "2-digit", minute: "2-digit" }}
                                showIcon={false}
                                label={getLocalizedMessage({intl, stringId: "timepicker.label"})}
                            />
                        </Row>
                    </Column>
                    <Column spacing="200">
                        <Text tag="label" type="b200" color="primary" htmlFor="my-input">
                            {getLocalizedMessage({intl, stringId: "modal.validationremarks"})}
                        </Text>
                        <Textarea
                            id="my-input"
                            value={remarks}
                            onChange={setRemarks}
                            type="text"
                        />
                    </Column>
                </Column>
                <ModalFooter>
                    <Column>
                        {!isFromAndToDateTimeEntered(dateTimeInput) &&
                            showWarning &&
                            <Alert type="warning"
                                   size="small">
                                {getLocalizedMessage({intl, stringId: "alert.timenotentered"})}
                            </Alert>}
                        {isStartTimeLaterThanEndTime(stateValues) &&
                            showWarning &&
                            <Alert type="warning"
                                   size="small">
                                {getLocalizedMessage({intl, stringId: "alert.time"})}
                            </Alert>}
                        {isDurationMoreThanTwelveHours(stateValues) &&
                            showWarning &&
                            <Alert type="warning"
                                   size="small">
                                {getLocalizedMessage({intl, stringId: "alert.longduration"})}
                            </Alert>}
                    </Column>
                    <Row alignmentHorizontal="right" widths="fit">
                        <Button type="secondary" size="medium" onClick={onClickCancel}>
                            {getLocalizedMessage({intl, stringId: "modal.cancel"})}
                        </Button>
                        <Button type="primary"
                                size="medium"
                                onClick={() => triggerMHEValidation(stateValues)}>
                            {getLocalizedMessage({intl, stringId: "modal.confirm"})}
                        </Button>
                    </Row>
                </ModalFooter>
            </Modal>
        </Row>
    )
}

export default injectIntl(NewValidationModal);



// WEBPACK FOOTER //
// ./src/components/commonui/mheValidation/NewValidationModal.js